import {useGatedValue, usePrevious} from '@wandb/weave/common/state/hooks';
import * as QueryResult from '@wandb/weave/common/state/queryGraph/queryResult';
import {
  DEFAULT_LIMIT,
  type Query,
  type QueryArg,
  type QueryField,
  type QueryTemplateArg,
  type Table,
  type Transform,
} from '@wandb/weave/common/util/vega3';
import * as GraphQL from 'graphql';
import {produce} from 'immer';
import * as _ from 'lodash';
import {useMemo} from 'react';
import {type Spec} from 'vega';

// eslint-disable-next-line import/no-cycle -- please fix if you can
import {type VegaSpecInfo} from '../components/property-editors/property-editors';
import * as BuiltinPanelDefs from '../components/Vega2/builtinPanelDefs';
import * as ApiSchemaTypes from '../generated/apiSchema';
import * as ApiSchema from '../generated/apiSchema.json';
import {type View} from '../state/graphql/vega2PanelQuery';
import {type RunsQueryContext} from '../state/runs/context';
import {type CustomRunColors} from '../state/views/customRunColors/types';
import {useQuery2} from '../util/apollo2-hooks';
import * as Filter from '../util/filters';
import * as QueryTS from '../util/queryts';
import type * as QueryTypes from '../util/queryTypes';
import {colorN, ROBIN16} from './colors';
import {isReservedKey} from './runs';
import * as VegaLib2 from './vega2';
import {type VegaPanelDef} from './vega2';

// backwards compatibility with weave refactor
export * from '@wandb/weave/common/util/vega3';

export const HIDDEN_FIELDS = ['defaultColorIndex'];

export const DEFAULT_TRANSFORM: Transform = {
  name: 'tableWithLeafColNames',
};

// When we don't have a configured transform, we use this.
// This is to handle legacy panels (which didn't set a transform
// and used the old full path column names transform)
export const TRANSFORM_WHEN_MISSING: Transform = {
  name: 'tableWithFullPathColNames',
};

export function fieldIsHidden(field: string) {
  return field !== '_step' && isReservedKey(field);
}
export function makeDupesBefore(arr: string[]): number[] {
  const counts: {[key: string]: number} = {};
  const result: number[] = [];
  for (const v of arr) {
    counts[v] = counts[v] != null ? counts[v] + 1 : 0;
    result.push(counts[v]);
  }
  return result;
}

export function toGraphql(
  query: Query,
  templates: {[template: string]: any},
  replaceTemplates: boolean = true
) {
  const queryType = getSchemaType('Query')!;
  let result = 'query {\n';
  const fields = query.queryFields.filter(f => f.name !== '');
  const dupesBefore = makeDupesBefore(fields.map(f => f.name));
  result += query.queryFields
    .filter((f: any) => f.name !== '')
    .map((f, i) =>
      toGraphqlField(
        f,
        dupesBefore[i],
        2,
        queryType.fields!.find(ff => f.name === ff.name)!,
        templates,
        replaceTemplates
      )
    )
    .join('');
  result += '}\n';
  return result;
}

export function indent(s: string, n: number) {
  let result = '';
  for (let i = 0; i < n; i++) {
    result += ' ';
  }
  return result + s;
}

export function toGraphqlField(
  field: QueryField,
  dupesBefore: number,
  depth: number,
  fieldSchema: ApiSchemaTypes.__Field,
  templates: {[template: string]: any},
  replaceTemplates: boolean = true
) {
  let prefix = field.name;
  if (dupesBefore !== 0) {
    prefix = `${field.name}${dupesBefore}: ${field.name}`;
  }
  let result = indent(prefix, depth);
  if (field.args != null) {
    const argStrings: string[] = [];
    for (const arg of field.args) {
      let val = arg.value;

      // if this is a template and we aren't replacing, put it inline as a string
      if (val in templates && !replaceTemplates) {
        argStrings.push(`${arg.name}: ${JSON.stringify(val)}`);
        continue;
      }

      if (val in templates) {
        val = templates[val];
      }
      const argType = fieldSchema.args.find(a => a.name === arg.name)!.type;
      argStrings.push(`${arg.name}: ${toGraphqlArg(val, argType)}`);
    }
    result += `(${argStrings.join(', ')})`;
  }
  if (field.fields == null || field.fields.length === 0) {
    result += '\n';
    return result;
  }
  result += ' {\n';
  const fieldType = getInnerType(fieldSchema.type)!;
  const fieldDupesBefore = makeDupesBefore(field.fields.map(f => f.name));
  for (const line of field.fields.map((f, i) =>
    toGraphqlField(
      f,
      fieldDupesBefore[i],
      depth + 2,
      fieldType.fields!.find(ff => f.name === ff.name)!,
      templates
    )
  )) {
    result += line;
  }
  result += indent('}\n', depth);
  return result;
}

export function toGraphqlArg(val: any, argType: ApiSchemaTypes.__Type): string {
  if (argType.name != null) {
    argType = getSchemaType(argType.name)!;
  }

  /* eslint-disable no-case-declarations */
  // FIXME: There are a few variables assigned in `case` statements that
  //        should be reworked. Ignore the issue here rather than fix and
  //        potentially introduce new bugs.
  switch (argType.kind) {
    case ApiSchemaTypes.__TypeKind.NonNull:
      return toGraphqlArg(val, argType.ofType!);
    case ApiSchemaTypes.__TypeKind.List:
      const arrayVal = val as any[];
      return (
        '[' +
        arrayVal.map(v => toGraphqlArg(v, argType.ofType!)).join(', ') +
        ']'
      );
    case ApiSchemaTypes.__TypeKind.InputObject:
      const objVal = val as {[key: string]: any};
      const objKeys = Object.keys(objVal);
      return (
        '{' +
        objKeys
          .map(
            k =>
              k +
              ': ' +
              toGraphqlArg(
                objVal[k],
                argType.inputFields!.find(f => f.name === k)!.type
              )
          )
          .join(', ') +
        '}'
      );
    case ApiSchemaTypes.__TypeKind.Scalar:
      if (/[F]ilter/.test(argType.name!)) {
        return JSON.stringify(JSON.stringify(Filter.toMongo(val)));
      } else {
        return JSON.stringify(val);
      }
    default:
      // shouldn't be hit
      return JSON.stringify(val);
  }
}

export function stripResultObj(result: any): any {
  if (_.isArray(result)) {
    return result.map(r => stripResultObj(r));
  } else if (_.isObject(result)) {
    const ret: {[key: string]: any} = {};
    _.forEach(result, (v: any, k) => {
      if (k === '__typename') {
        // skip
      } else {
        ret[k] = stripResultObj(v);
      }
    });
    return ret;
  }
  return result;
}

export function getSchemaType(typeName: string) {
  for (const schemaType of ApiSchema.__schema.types) {
    if (schemaType.name === typeName) {
      return schemaType as ApiSchemaTypes.__Type;
    }
  }
  return null;
}

export function getInnerType(type: ApiSchemaTypes.__Type) {
  while (type.ofType != null) {
    type = type.ofType;
  }
  return getSchemaType(type.name!);
}

export function getDefaultArgFromSchemaArgType(
  schemaArgType: ApiSchemaTypes.__Type
) {
  if (schemaArgType.kind === 'LIST') {
    return [];
  }
  if (schemaArgType.name === 'Int') {
    return 0;
  }
  if (schemaArgType.name === 'String') {
    return '';
  }
  if (schemaArgType.name === 'RunFilter') {
    return Filter.EMPTY_FILTERS;
  }
  return null;
}

/**
 * Prepopulates required args with defaults
 * @param schemaField
 */
export function getDefaultFieldFromSchemaField(
  schemaField: ApiSchemaTypes.__Field
) {
  const args = [];
  for (const arg of schemaField.args) {
    if (arg.type.kind === 'NON_NULL') {
      args.push({
        name: arg.name,
        value: getDefaultArgFromSchemaArgType(arg.type.ofType!),
      });
    }
  }
  return {
    name: schemaField?.name,
    args: args.length > 0 ? args : undefined,
    fields: [],
  };
}

export function transformQueryResultToTable(
  query: Query,
  transform: Transform,
  queryResult: QueryResult.Row[]
): Table {
  queryResult.forEach(result => {
    if (result.runSets != null) {
      result.runSets.forEach((rs: any) => {
        if (rs.groupKeys != null) {
          rs.groupKeys = rs.groupKeys.join('_');
        }
      });
    }
  });
  const rows =
    transform.name === 'tableWithLeafColNames'
      ? QueryResult.flattenNested(queryResult)
      : QueryResult.flattenNestedOld(queryResult);

  const keys = new Set<string>();
  rows.forEach(row => {
    Object.keys(row).forEach(key => {
      keys.add(key);
    });
  });
  return {
    cols: Array.from(keys),
    rows,
  };
}

export function useVegaQuery(
  query: Query,
  transform: Transform,
  templateVals: {[template: string]: any},
  skip?: boolean
) {
  const stringQuery = useMemo(
    () => toGraphql(query, templateVals),
    [query, templateVals]
  );
  const {
    loading,
    result: rawResult,
    slow,
    initialLoading,
  } = useQuery2(stringQuery, skip);

  const {cols, rows} = useMemo(() => {
    const result = rawResult ? [stripResultObj(rawResult)] : [];
    return transformQueryResultToTable(query, transform, result);
  }, [query, rawResult, transform]);

  // Performance: Only send a new row result if rows differs from the previous
  // version that we've sent. Running vega can be very expensive, so we try
  // to keep rows ref-equal as much as possible. Typically when polling there
  // is no change.
  // We use a heuristic equality check rather than a full deep-equal, since
  // this hook runs whenever the caller component renders.
  const previousRows = usePrevious(rows);
  const gatedRows = useGatedValue(
    rows,
    curRows =>
      previousRows == null ||
      curRows.length !== previousRows.length ||
      (curRows.length > 0 &&
        (!_.isEqual(curRows[0], previousRows[0]) ||
          !_.isEqual(
            curRows[curRows.length - 1],
            previousRows[previousRows.length - 1]
          )))
  );

  cols.sort();

  return {loading, initialLoading, result: gatedRows, cols, slow};
}

export function getVegaPanelDef(
  config: VegaSpecInfo,
  views: View[]
): VegaPanelDef | null {
  const panelDefID = getPanelDefID(config);
  if (_.startsWith(panelDefID, 'lib:')) {
    const id = panelDefID.split(':')[1];
    const view = _.find(views, v => v.id === id);
    if (view == null) {
      return null;
    }
    return {
      name: view.name,
      displayName: view.name,
      description: view.description,
      spec: view.spec,
    };
  } else if (panelDefID === 'custom') {
    return config.customPanelDef!;
  } else {
    return BuiltinPanelDefs.getPanelDef(panelDefID);
  }
}

export function getSpec(config: VegaSpecInfo, views: View[]): Spec | null {
  const panelDef = getVegaPanelDef(config, views);
  if (panelDef == null) {
    return null;
  }
  const {parsedSpec} = VegaLib2.parseSpecJSON(panelDef.spec);
  return parsedSpec;
}

export function getPanelDefID(config: VegaSpecInfo) {
  return config.panelDefId || BuiltinPanelDefs.IDS[0];
}

/* eslint-disable no-template-curly-in-string */
export const fixedRunSetsQuery: Query = {
  queryFields: [
    {
      name: 'runSets',
      args: [{name: 'runSets', value: '${runSets}'}],
      fields: [
        {name: 'id', fields: []},
        {name: 'name', fields: []},
      ],
    },
  ],
};

export function queryTemplates(
  pageQuery: QueryTypes.Query,
  runsQueryContext: RunsQueryContext
): {
  templateArgs: QueryTemplateArg[];
  templateVals: {[template: string]: any};
} {
  const runSetFilters = (rs: QueryTypes.RunSetQuery) => {
    const filters = [rs.filters];
    if (runsQueryContext.mergeFilters != null) {
      filters.push(runsQueryContext.mergeFilters);
    }
    if (rs.selections != null) {
      filters.push(rs.selections);
    }
    return filters.length === 1 ? rs.filters : Filter.And(filters);
  };

  return {
    templateArgs: [
      {
        typeName: 'Query',
        fieldName: 'runSets',
        argName: 'runSets',
        value: '${runSets}',
      },
    ],
    templateVals: {
      '${runSets}': (pageQuery.runSets ?? [])
        .filter(rs => rs.enabled)
        .map(rs => ({
          entityName: rs.entityName ?? runsQueryContext.entityName,
          projectName: rs.projectName ?? runsQueryContext.projectName,
          filters: runSetFilters(rs),
          order: QueryTS.sortToOrderString(rs.sort),
          grouping: rs.grouping,
        })),
    },
  };
}
/* eslint-enable no-template-curly-in-string */

function queryDocToQuery(rawQuery: string): Query | null {
  const doc = GraphQL.parse(rawQuery);
  if (doc.definitions.length !== 1) {
    return null;
  }
  const queryNode = doc.definitions[0];
  if (
    queryNode.kind !== 'OperationDefinition' ||
    queryNode.operation !== 'query'
  ) {
    return null;
  }

  const parseValue = (val: GraphQL.ValueNode): any => {
    switch (val.kind) {
      case 'IntValue':
        return Number(val.value);
      case 'FloatValue':
        return Number(val.value);
      case 'StringValue':
        return val.value;
      case 'BooleanValue':
        return val.value;
      case 'NullValue':
        return null;
      case 'EnumValue':
        return val.value;
      case 'ListValue':
        return val.values.map(parseValue);
      case 'ObjectValue':
        return Object.assign(
          {},
          ...val.fields.map(field => ({
            [field.name.value]: parseValue(field.value),
          }))
        );
      default:
        throw new Error('invalid value kind ' + val.kind);
    }
  };

  const parseArg = (arg: GraphQL.ArgumentNode): QueryArg => {
    return {
      name: arg.name.value,
      value: parseValue(arg.value),
    };
  };

  const parseSelection = (sel: GraphQL.SelectionNode): QueryField => {
    if (sel.kind !== 'Field') {
      throw new Error('only fields allowed');
    }

    let args: QueryArg[] | undefined;
    if (sel.arguments != null && sel.arguments.length > 0) {
      args = sel.arguments.map(parseArg);
    }

    let fields: QueryField[] = [];
    if (sel.selectionSet != null) {
      fields = sel.selectionSet.selections.map(parseSelection);
    }

    return {
      name: sel.name.value,
      args,
      fields,
    };
  };

  return {
    queryFields: queryNode.selectionSet.selections.map(parseSelection),
  };
}

function validateAndMergeQuery(
  query: Query,
  fixedFields: QueryField[],
  templateArgs: QueryTemplateArg[]
): Query | null {
  const processArg = (
    arg: QueryArg,
    type: ApiSchemaTypes.__InputValue | undefined
  ): QueryArg => {
    if (type == null) {
      throw new Error('unknown arg ' + arg.name);
    }
    // TODO: better validation
    return arg;
  };
  const processField = (
    field: QueryField,
    fixed: QueryField | undefined,
    type: ApiSchemaTypes.__Field
  ): QueryField => {
    if (field.args != null) {
      const fixedArgs = fixed?.args ?? [];

      const extra = field.args.filter(
        a => fixedArgs.find(aa => a.name === aa.name) == null
      );
      field = {
        ...field,
        args: [
          ...fixedArgs,
          ...extra.map(arg =>
            processArg(
              arg,
              type.args.find(a => a.name === arg.name)
            )
          ),
        ],
      };
    }
    const ftype = getSchemaType(getInnerType(type.type)!.name!)!;
    if (fixed != null) {
      field = {
        ...field,
        fields: [
          ...fixed.fields.filter(
            f => field.fields.find(ff => f.name === ff.name) == null
          ),
          ...field.fields.map(f => {
            const ffield = (ftype.fields ?? []).find(ft => f.name === ft.name);
            if (ffield == null) {
              throw new Error(`unknown field ${f.name} on ${ftype.name}`);
            }
            return processField(
              f,
              (fixed?.fields ?? []).find(ff => f.name === ff.name),
              ffield
            );
          }),
        ],
      };
    }
    return field;
  };
  const queryType = getSchemaType('Query')!;
  return {
    queryFields: processField(
      {name: 'query', fields: query.queryFields},
      {name: 'query', fields: fixedFields},
      {
        name: 'query',
        args: [],
        type: queryType,
        isDeprecated: false,
      }
    ).fields,
  };
}

export function parseQuery(
  rawQuery: string,
  fixedFields: QueryField[],
  templateArgs: QueryTemplateArg[]
): Query | null {
  let query: Query | null = null;
  try {
    query = queryDocToQuery(rawQuery);
    if (query == null) {
      return null;
    }
    return validateAndMergeQuery(query, fixedFields, templateArgs);
  } catch {
    return null;
  }
}

export function valueCharCount(value: string | number | any[]) {
  if (typeof value === 'string') {
    return value.length + 2;
  }
  if (typeof value === 'number') {
    return value.toString().length;
  }
  if (Array.isArray(value)) {
    let result = 0;
    value.forEach(val => {
      result += valueCharCount(val) + 2;
    });
    return result;
  }
  return 6;
}

export function argsCharCount(args: QueryArg[]) {
  let result = 0;
  args.forEach(arg => {
    result += arg.name.length + 2;
    result += valueCharCount(arg.value) + 2;
  });
  return result;
}

export function injectAutoFields(query: Query) {
  return produce(query, draft => {
    const runSetsQueryFields = draft.queryFields.find(
      f => f.name === 'runSets'
    );
    if (runSetsQueryFields != null) {
      runSetsQueryFields.fields.push({name: '_defaultColorIndex', fields: []});
      const historyTableFields = runSetsQueryFields.fields.filter(
        f => f.name === 'historyTable'
      );
      historyTableFields.forEach(field => {
        const extraKeys = field.args?.find(a => a.name === 'extraKeys');
        if (
          extraKeys != null &&
          extraKeys.value.find((a: string) => a === '_step') == null
        ) {
          extraKeys.value.push('_step');
        }
      });
      // inject limit so old charts without the limit still work
      const limitArg = runSetsQueryFields.args?.find(a => a.name === 'limit');
      if (limitArg == null) {
        runSetsQueryFields.args?.push({name: 'limit', value: DEFAULT_LIMIT});
      }
    }
  });
}

export function getViewedStep(query: Query) {
  const historyTableKeys = query.queryFields
    .find(f => f.name === 'runSets')
    ?.fields.filter(f => f.name === 'historyTable');
  if (historyTableKeys && historyTableKeys.length > 0) {
    return historyTableKeys[0].args?.find(a => a.name === 'index')?.value;
  }
  return null;
}

export function createIndsQuery(query: Query) {
  const historyTableKeys = query.queryFields
    .find(f => f.name === 'runSets')
    ?.fields.filter(f => f.name === 'historyTable');
  const args: any[] | undefined = historyTableKeys
    ?.map(qf => {
      return qf.args?.filter(a => a.name === 'tableKey').map(a => a.value);
    })
    .flat();
  const queryArgs = args?.map(a => [a, '_step']);
  const fields =
    queryArgs && queryArgs.length > 0
      ? queryArgs?.map(arg => {
          return {
            name: 'history',
            args: [{name: 'keys', value: arg}],
            fields: [],
          };
        })
      : [
          {
            name: 'history',
            args: [{name: 'keys', value: ['_step']}],
            fields: [],
          },
        ];
  return {
    queryFields: [
      {
        name: 'runSets',
        // eslint-disable-next-line no-template-curly-in-string
        args: [{name: 'runSets', value: '${runSets}'}],
        fields,
      },
    ],
  };
}

export function removeQueryIndex(query: Query) {
  const result: Query = produce(query, draft => {
    draft.queryFields
      .find(f => f.name === 'runSets')
      ?.fields.filter(f => f.name === 'historyTable')
      ?.forEach(field => {
        field.args
          ?.slice()
          .reverse()
          .forEach((arg, index) => {
            if (arg.name === 'index') {
              field.args?.splice(field.args.length - 1 - index, 1);
            }
          });
      });
  });
  return result;
}

export function computeRunColors(
  queryResult: Array<{[key: string]: any}>,
  customRunColors: CustomRunColors | undefined
) {
  if (customRunColors == null) {
    return queryResult;
  }
  const pallete = _.range(ROBIN16.length).map(i => colorN(i, ROBIN16));

  const res = queryResult.map(row => {
    let color: string;
    // eslint-disable-next-line no-prototype-builtins
    if (customRunColors!.hasOwnProperty(row.id)) {
      color = customRunColors![row.id];
    } else if (row._defaultColorIndex != null) {
      color = pallete[row._defaultColorIndex % ROBIN16.length];
    } else {
      color = 'black';
    }
    const resultRow: {[key: string]: any} = {...row, color};
    delete resultRow._defaultColorIndex;
    return resultRow;
  });
  return res;
}

export function computeRunNames(
  queryResult: Array<{[key: string]: any}>,
  customRunNames: {[key: string]: string} | undefined
) {
  if (!customRunNames || Object.keys(customRunNames).length === 0) {
    return queryResult;
  }
  return queryResult.map(row => {
    const customRunName = customRunNames[row.id];
    return customRunName ? {...row, name: customRunName} : row;
  });
}

export function updateMatchingKeys(userQuery: Query, newQuery: Query): Query {
  // Checks if a user is changing their query from summary to history
  // (or vice versa), or summaryTable to historyTable (or vice versa)
  // and keeps existing query information so they don't have to reinput
  const updatedFields = newQuery.queryFields[0].fields.map((value, index) => {
    const prevField = userQuery.queryFields[0].fields[index];
    const updatedField = value;
    if (prevField?.args == null || updatedField?.args == null) {
      return updatedField ? updatedField : prevField;
    }
    if (
      updatedField.name === 'summaryTable' &&
      prevField.name === 'historyTable'
    ) {
      updatedField.args = prevField.args.filter(
        arg => arg.name === 'tableKey' || arg.name === 'tableColumns'
      );
    } else if (
      updatedField.name === 'historyTable' &&
      prevField.name === 'summaryTable'
    ) {
      // add in extra keys arg so it pops up
      const extraKeys = updatedField.args.find(arg => arg.name === 'extraKeys');
      updatedField.args = [...prevField.args];
      if (extraKeys != null) {
        updatedField.args.push(extraKeys);
      }
    } else if (
      (updatedField.name === 'summary' && prevField.name === 'history') ||
      (updatedField.name === 'history' && prevField.name === 'summary')
    ) {
      const keyNameIndex = prevField.args.findIndex(arg => arg.name === 'keys');
      if (keyNameIndex !== -1) {
        updatedField.args[keyNameIndex].value =
          prevField.args[keyNameIndex].value;
      }
    }
    return updatedField;
  });
  return {
    queryFields: [
      {
        args: newQuery.queryFields[0].args,
        fields: updatedFields,
        name: 'runSets',
      },
    ],
  };
}
